import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Info } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { buildBreadcrumbLd } from "@/lib/seo";
import { END_LEASE_SIZES, HOURLY_RATES_TTC, VAT_RATE, formatCHF, labelSize } from "@/lib/pricing";
import { useSeoT } from "@/lib/seoPagesI18n";

// indexable=false tant qu'aucune grille de prix validée n'existe (déménagement /
// débarras) : on publie les facteurs de prix réels, jamais un tarif non validé.
const CFG = {
  demenagement: { serviceSlug: "demenagement", indexable: false, qParam: "move" },
  debarras: { serviceSlug: "debarras", indexable: false, qParam: "clear" },
  "nettoyage-fin-de-bail": { serviceSlug: "nettoyage", indexable: true, qParam: "clean" },
};

export default function TarifsService() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t: st } = useSeoT();
  const cfg = CFG[slug];
  if (!cfg) return <NotFound />;

  const tt = st.tarifs;
  const content = tt.content[slug];
  const cardName = tt.cards[slug].name;
  const path = `/tarifs/${slug}`;
  const bc = [
    { name: st.bc.home, path: "/" },
    { name: st.bc.tarifs, path: "/tarifs" },
    { name: cardName, path },
  ];
  const dateLocale = { fr: "fr", de: "de", en: "en" }[lang] || "fr";

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid={`tarifs-${slug}`}>
      <Seo title={content.title} description={content.description} path={path} noindex={!cfg.indexable} jsonLd={buildBreadcrumbLd(toLdItems(bc))} />
      <Header />

      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1080px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {content.title}
          </h1>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-2xl">{content.intro}</p>
        </div>
      </section>

      {slug === "nettoyage-fin-de-bail" && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
          <div className="mx-auto" style={{ maxWidth: "1080px" }}>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{tt.gridTitle}</h2>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-gray-100">
              <table className="w-full text-sm" data-testid="tarifs-table">
                <thead className="bg-[#F7F7F8] text-left">
                  <tr>
                    <th className="p-4 font-bold">{tt.th.home}</th>
                    <th className="p-4 font-bold">{tt.th.dur}</th>
                    <th className="p-4 font-bold">{tt.th.range}</th>
                  </tr>
                </thead>
                <tbody>
                  {END_LEASE_SIZES.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`tarifs-row-${s.id}`}>
                      <td className="p-4 font-semibold">{labelSize(s, dateLocale)}</td>
                      <td className="p-4 text-[#4B5563]">{s.hours_min}–{s.hours_max} h · {s.persons} {tt.pers}</td>
                      <td className="p-4 font-extrabold text-[#0A0F2C]">{formatCHF(s.ttc_min)} – {formatCHF(s.ttc_max)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 flex items-start gap-2 text-[13px] text-[#6B7280]"><Info size={15} className="shrink-0 mt-0.5" /> {tt.note(formatCHF(HOURLY_RATES_TTC.une_fois), VAT_RATE)}</p>
          </div>
        </section>
      )}

      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10" style={{ maxWidth: "1080px" }}>
          <div>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{tt.factorsTitle}</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">{tt.factorsSub}</p>
            <button onClick={() => navigate(`/devis?service=${cfg.qParam}`)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-6 py-3 text-sm font-extrabold transition-all" data-testid="tarifs-cta-quote">
              {tt.factorsCta} <ArrowRight size={14} />
            </button>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {content.factors.map((f, i) => (
              <li key={i} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-3" data-testid={`tarifs-factor-${i}`}>
                <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] font-extrabold text-xs">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[14.5px] text-[#0A0F2C] leading-relaxed font-medium">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-12 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto flex flex-wrap items-center gap-4 justify-between" style={{ maxWidth: "1080px" }}>
          <Link to={`/services/${cfg.serviceSlug}`} className="inline-flex items-center gap-2 text-[#2D5BFF] font-bold text-sm hover:gap-3 transition-all" data-testid="tarifs-service-link">
            <Check size={15} /> {tt.moreService(cardName)} <ArrowRight size={14} />
          </Link>
          <Link to="/tarifs" className="text-sm font-semibold text-[#6B7280] hover:text-[#0A0F2C]">← {tt.allTarifs}</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
